import { useState, useEffect, useRef, useCallback } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Lock, Clock, CalendarClock, Pencil, Check, CheckCircle2, Sparkles, Trash2, RotateCw } from "lucide-react";
import PitchView from "../../components/football/PitchView";
import PlayerPickerModal from "../../components/football/PlayerPickerModal";
import PowerUpsPanel from "../../components/football/PowerUpsPanel";
import CaptainSelector from "../../components/football/CaptainSelector";
import BudgetTracker from "../../components/football/BudgetTracker";
import ProfileModal from "../../components/shared/ProfileModal";
import { FootieBall } from "../../components/football/FootieIcon";
import { FootieBuildModal } from "../../components/football/FootieModals";
import { useFb, useCountdown, validateSquad, positionCounts, POSITION_RULES, FORMATIONS, FORMATION_LIST, DEFAULT_FORMATION, POSITION_WORD, emptySlotsForFormation, deriveFormation } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";
import { hapticSuccess, hapticMedium } from "../../utils/haptics";
import { toast } from "sonner";

const dayLabel = (md) => new Date(`${md}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric" });
const fullDayLabel = (md) => new Date(`${md}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" });

// ---- Forced team-name prompt (Item 3) ----
function TeamNamePrompt({ onSave }) {
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const trimmed = val.trim();
  const valid = trimmed.length >= 3 && trimmed.length <= 30 && /^[A-Za-z0-9 ]+$/.test(trimmed);
  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    const ok = await onSave(trimmed);
    if (!ok) setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" data-testid="team-name-prompt">
      <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-slate-800 p-5">
        <h2 className="text-lg font-extrabold text-white">Name your team</h2>
        <p className="mt-1 text-sm text-slate-400">This name appears on leaderboards for every match day.</p>
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          maxLength={30}
          placeholder="e.g. Thunderbolts XI"
          className="mt-4 w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2.5 text-base font-bold text-white outline-none focus:border-amber-500"
          data-testid="team-name-prompt-input"
        />
        <p className="mt-1 text-[11px] text-slate-500">3–30 characters · letters, numbers & spaces</p>
        <button
          onClick={submit}
          disabled={!valid || saving}
          className="mt-4 w-full rounded-xl wc-bg-gold py-3 text-sm font-extrabold text-slate-900 disabled:opacity-40"
          data-testid="team-name-prompt-continue"
        >
          {saving ? "Saving…" : "Continue"}
        </button>
      </div>
    </div>
  );
}

// ---- Match-day picker pills (Item 4) ----
const STATUS_STYLE = {
  scored: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  locked: "border-slate-600 bg-slate-800/60 text-slate-400",
  missed: "border-slate-700 bg-slate-800/40 text-slate-500",
  submitted: "border-amber-400 bg-amber-400/15 text-amber-200",
  draft: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  open: "border-slate-600 bg-card text-foreground/80",
};

function DayPicker({ days, selected, onSelect }) {
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);
  // Auto-scroll the active/selected day pill into the centre on load and whenever the
  // selection changes (covers both default-load and navigation-from-Matches scenarios).
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    }
  }, [selected, days]);
  return (
    <div ref={scrollerRef} className="mb-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="match-day-picker">
      {days.map((d) => {
        const active = d.match_day === selected;
        const style = STATUS_STYLE[d.status] || STATUS_STYLE.open;
        return (
          <button
            key={d.match_day}
            ref={active ? activeRef : null}
            onClick={() => onSelect(d.match_day)}
            className={`flex min-w-[64px] shrink-0 flex-col items-center rounded-xl border px-3 py-2 text-center transition-all ${style} ${active ? "ring-2 ring-amber-400" : ""}`}
            data-testid={`day-pill-${d.match_day}`}
          >
            <span className="text-xs font-extrabold">{dayLabel(d.match_day)}</span>
            <span className="mt-0.5 flex items-center gap-0.5 text-[9px] font-bold uppercase">
              {d.status === "scored" && <>{d.points ?? 0} pts</>}
              {d.status === "locked" && <><Lock size={9} /> Locked</>}
              {d.status === "missed" && "—"}
              {d.status === "submitted" && <><Check size={9} /> In</>}
              {d.status === "draft" && "Draft"}
              {d.status === "open" && "Build"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---- Match-day fixtures strip (Issue 4) ----
// Uses 3-letter team codes (from /football/teams) so every fixture fits on one line.
function MatchDayFixturesStrip({ fixtures, teams }) {
  if (!fixtures || fixtures.length === 0) return null;
  const codeById = new Map((teams || []).map((t) => [t.api_football_team_id, t.code]));
  const abbr = (t) => {
    if (!t) return "TBD";
    return codeById.get(t.id) || (t.name || "TBD").slice(0, 3).toUpperCase();
  };
  return (
    <div className="mb-3 flex items-center gap-2 overflow-x-auto rounded-xl border border-border bg-card px-3 py-2 scrollbar-hide" data-testid="matchday-fixtures-strip">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-300/80">Fixtures</span>
      <div className="flex shrink-0 items-center gap-1.5 text-[11px]">
        {fixtures.map((fx, i) => (
          <span key={fx.id} className="shrink-0 whitespace-nowrap" data-testid={`fixtures-strip-item-${fx.id}`}>
            {i > 0 && <span className="mr-1.5 text-border">|</span>}
            <span className="font-bold text-foreground/80">{abbr(fx.home_team)}</span>
            <span className="mx-0.5 text-muted-foreground">v</span>
            <span className="font-bold text-foreground/80">{abbr(fx.away_team)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function FootballSquadBuilderPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { matchDay: routeMatchDay } = useParams();
  const { dbUser, setDbUser } = useAuth();
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [squad, setSquad] = useState([]);
  const [formation, setFormation] = useState(DEFAULT_FORMATION);
  const [formationConfirm, setFormationConfirm] = useState({ open: false, target: null, message: "", removeIds: [] });
  const [isLocked, setIsLocked] = useState(false);
  const [isFinal, setIsFinal] = useState(false);
  const [isAiBuilt, setIsAiBuilt] = useState(false);
  const [consumablesUsed, setConsumablesUsed] = useState({});
  const [inventory, setInventory] = useState({ super_sub: 0, no_penalty_shield: 0 });
  const [footieOpen, setFootieOpen] = useState(false);
  const [footieBuilding, setFootieBuilding] = useState(false);
  const [footieLoading, setFootieLoading] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [scored, setScored] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [teams, setTeams] = useState([]);
  const [dayFixtures, setDayFixtures] = useState([]);
  const [picker, setPicker] = useState({ open: false, position: null, mode: "add", current: null });
  const [tab, setTab] = useState("pitch");
  const [profileOpen, setProfileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teamName, setTeamName] = useState(dbUser?.football_team_name || "");
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const profilePrompted = useRef(false);
  const nameTimer = useRef(null);
  const initRef = useRef(false);
  const cd = useCountdown(lockTime);

  // Load match days + teams once auth resolves (Issue 4: wait for dbUser so the
  // authenticated request carries a token; runs only once via initRef).
  useEffect(() => {
    if (initRef.current || !dbUser) return;
    initRef.current = true;
    (async () => {
      let dayList = [];
      try { const r = await fb.get("/football/squad/days"); dayList = r.days || []; } catch { /* ignore */ }
      setDays(dayList);
      const future = dayList.filter((d) => !d.kickoff_passed);
      const fallback = (future[0] || dayList[dayList.length - 1])?.match_day || null;
      // Issue 2: honor the match-day route param when present and valid.
      const target = (routeMatchDay && dayList.some((d) => d.match_day === routeMatchDay)) ? routeMatchDay : fallback;
      setSelectedDay(target);
      const hasName = !!dbUser?.football_team_name;
      const hasAnySquad = dayList.some((d) => d.has_squad);
      if (!hasName && !hasAnySquad) setShowNamePrompt(true);
      else if (hasName) setTeamName(dbUser.football_team_name);
      try { const t = await fb.get("/football/teams"); setTeams(t.teams || []); } catch { /* ignore */ }
      try { setInventory(await fb.get("/football/consumables/inventory")); } catch { /* ignore */ }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  // Load the selected day's squad
  const loadSquad = useCallback(async () => {
    if (!selectedDay) return;
    try {
      const d = await fb.get(`/football/squad/${selectedDay}`);
      const sq = d.squad || [];
      setSquad(sq);
      setFormation(d.formation || deriveFormation(sq));
      setIsLocked(!!d.is_locked);
      setIsFinal(!!d.is_final);
      setIsAiBuilt(!!d.is_ai_built);
      setConsumablesUsed(d.consumables_used || {});
    } catch {
      setSquad([]); setFormation(DEFAULT_FORMATION); setIsLocked(false); setIsFinal(false); setIsAiBuilt(false); setConsumablesUsed({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  useEffect(() => {
    if (!selectedDay) return;
    const meta = days.find((d) => d.match_day === selectedDay);
    setReadOnly(!!meta?.read_only);
    setScored(meta?.status === "scored");
    setLockTime(meta?.lock_time_utc || null);
    setEditMode(false);
    loadSquad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, days]);

  // Fixtures for the selected match day (Issue 4 — fixtures strip).
  useEffect(() => {
    if (!selectedDay) { setDayFixtures([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fb.get("/football/fixtures", { match_day: selectedDay });
        if (!cancelled) setDayFixtures(r.fixtures || []);
      } catch { if (!cancelled) setDayFixtures([]); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  const canEdit = !readOnly && !isFinal && (!isLocked || editMode) && !showNamePrompt;

  const autoSave = useCallback(async (next, fmt) => {
    if (!selectedDay || readOnly || isFinal) return;
    if (!profilePrompted.current && !dbUser?.birth_year) { profilePrompted.current = true; setTimeout(() => setProfileOpen(true), 0); }
    try {
      await fb.post(`/football/squad/${selectedDay}`, {
        squad: next.map((s) => ({ player_id: s.player_id, is_captain: !!s.is_captain, is_vice_captain: !!s.is_vice_captain, is_super_sub: !!s.is_super_sub })),
        formation: fmt || formation,
      });
    } catch { /* draft best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser, selectedDay, readOnly, isFinal, formation]);

  const updateSquad = (next) => { setSquad(next); autoSave(next); };

  // ---- Formation switching (Issue 3) ----
  const applyFormation = (target, nextSquad) => {
    setFormation(target);
    if (nextSquad) { setSquad(nextSquad); autoSave(nextSquad, target); }
    else autoSave(squad, target);
  };

  const changeFormation = (target) => {
    if (!canEdit || target === formation) return;
    const shape = FORMATIONS[target];
    const counts = positionCounts(squad);
    const removeIds = [];
    const parts = [];
    ["GK", "DEF", "MID", "FWD"].forEach((p) => {
      const over = Math.max(0, (counts[p] || 0) - shape[p]);
      if (over > 0) {
        const inPos = squad.filter((s) => !s.is_super_sub && s.position === p).sort((a, b) => (a.credits || 0) - (b.credits || 0));
        inPos.slice(0, over).forEach((pl) => removeIds.push(pl.player_id));
        parts.push(`${over} ${POSITION_WORD[p]}${over > 1 ? "s" : ""}`);
      }
    });
    if (removeIds.length === 0) { applyFormation(target); return; }
    setFormationConfirm({ open: true, target, message: `Changing to ${target} will remove ${parts.join(" and ")} from your squad. Continue?`, removeIds });
  };

  const confirmFormationChange = () => {
    const { target, removeIds } = formationConfirm;
    const nextSquad = squad.filter((s) => !removeIds.includes(s.player_id));
    applyFormation(target, nextSquad);
    setFormationConfirm({ open: false, target: null, message: "", removeIds: [] });
  };

  const saveTeamName = async (name) => {
    try {
      const res = await fb.post("/football/squad/team-name", { team_name: name });
      if (setDbUser && dbUser) setDbUser({ ...dbUser, football_team_name: res.team_name });
      setTeamName(res.team_name);
      return true;
    } catch { toast.error("Could not save team name"); return false; }
  };

  const onTeamNameChange = (val) => {
    setTeamName(val);
    if (nameTimer.current) clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(() => { if (val.trim().length >= 3) saveTeamName(val.trim()); }, 700);
  };

  const counts = positionCounts(squad);
  const filled = squad.filter((s) => !s.is_super_sub).length;
  const emptySlots = canEdit ? emptySlotsForFormation(squad, formation) : null;

  const openAdd = (position) => { if (canEdit) setPicker({ open: true, position, mode: "add", current: null }); };
  const openSwap = (slot) => { if (canEdit) setPicker({ open: true, position: slot.position, mode: "swap", current: slot }); };

  const handlePick = (player) => {
    const slot = {
      player_id: player.id, name: player.name, position: player.position,
      team_id: player.team?.id, team_name: player.team?.name, credits: player.credits,
      jersey_number: player.jersey_number, photo_url: player.photo_url,
      is_captain: false, is_vice_captain: false, is_super_sub: false,
    };
    let next;
    if (picker.mode === "swap" && picker.current) {
      next = squad.map((s) => s.player_id === picker.current.player_id
        ? { ...slot, is_captain: s.is_captain, is_vice_captain: s.is_vice_captain } : s);
    } else {
      if (filled >= 11) { toast.error("Squad full (11 players)"); return; }
      next = [...squad, slot];
    }
    updateSquad(next);
    setPicker({ open: false, position: null, mode: "add", current: null });
  };

  const handleRemove = (player) => {
    updateSquad(squad.filter((s) => s.player_id !== player.player_id));
    setPicker({ open: false, position: null, mode: "add", current: null });
  };

  const setCaptain = (pid) => { hapticMedium(); updateSquad(squad.map((s) => ({ ...s, is_captain: s.player_id === pid, is_vice_captain: s.is_vice_captain && s.player_id !== pid }))); };
  const setVC = (pid) => { hapticMedium(); updateSquad(squad.map((s) => ({ ...s, is_vice_captain: s.player_id === pid }))); };

  // ---- FOOTIE — AI squad builder ----
  const reloadSquad = async () => {
    try {
      const d = await fb.get(`/football/squad/${selectedDay}`);
      setSquad(d.squad || []);
      setFormation(d.formation || deriveFormation(d.squad || []));
      setIsLocked(!!d.is_locked);
      setIsFinal(!!d.is_final);
      setIsAiBuilt(!!d.is_ai_built);
    } catch { /* ignore */ }
  };

  const buildWithFootie = async (fmt) => {
    if (footieBuilding) return;
    setFootieBuilding(true);
    try {
      await fb.post("/football/footie/auto-build", { match_day: selectedDay, formation: fmt });
      setFootieOpen(false);
      setFootieBuilding(false);
      setFootieLoading(true);              // 1–2s FOOTIE ball animation
      await new Promise((r) => setTimeout(r, 1400));
      await reloadSquad();
      setFootieLoading(false);
      hapticSuccess();
      toast.success("FOOTIE built your squad from today's playing teams. Review and tap Lock to submit.");
    } catch (e) {
      setFootieBuilding(false);
      setFootieLoading(false);
      toast.error(e?.response?.data?.detail || "FOOTIE couldn't build a squad for this match day");
    }
  };

  const clearAll = async () => {
    setClearConfirm(false);
    try {
      await fb.post(`/football/squad/${selectedDay}/clear`);
      setSquad([]); setIsAiBuilt(false); setFormation(DEFAULT_FORMATION);
      toast.success("Squad cleared. Start fresh.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear squad");
    }
  };

  const errors = validateSquad(squad);
  const canLock = errors.length === 0 && !cd.passed && canEdit;

  const doLock = async () => {
    try {
      await fb.post(`/football/squad/${selectedDay}`, { squad: squad.map((s) => ({ player_id: s.player_id, is_captain: !!s.is_captain, is_vice_captain: !!s.is_vice_captain, is_super_sub: !!s.is_super_sub })), formation });
      await fb.post(`/football/squad/${selectedDay}/lock`);
      hapticSuccess();
      setIsLocked(true);
      setEditMode(false);
      toast.success("Squad locked! Good luck 🏆");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.errors?.[0] || "Could not lock squad");
    }
  };

  const pickedIds = new Set(squad.map((s) => s.player_id));
  const budgetRemaining = 100 - squad.filter((s) => !s.is_super_sub).reduce((a, s) => a + (s.credits || 0), 0);
  const lockLabel = lockTime ? new Date(lockTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";

  // Submitted (locked, not final, editable until kickoff) and not in edit mode
  const submittedView = isLocked && !isFinal && !readOnly && !editMode;
  const showLockBar = !loading && !showNamePrompt && (canEdit || submittedView);

  return (
    <div className="football-theme min-h-screen bg-background pb-40" data-testid="football-squad-builder">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      {showNamePrompt && <TeamNamePrompt onSave={async (n) => { const ok = await saveTeamName(n); if (ok) setShowNamePrompt(false); return ok; }} />}

      {formationConfirm.open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" data-testid="formation-confirm">
          <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-slate-800 p-5">
            <h2 className="text-lg font-extrabold text-white">Change formation?</h2>
            <p className="mt-2 text-sm text-slate-300">{formationConfirm.message}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setFormationConfirm({ open: false, target: null, message: "", removeIds: [] })} className="flex-1 rounded-xl border border-slate-600 py-2.5 text-sm font-bold text-slate-300" data-testid="formation-confirm-cancel">Cancel</button>
              <button onClick={confirmFormationChange} className="flex-1 rounded-xl wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900" data-testid="formation-confirm-ok">Continue</button>
            </div>
          </div>
        </div>
      )}

      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="builder-back"><ChevronLeft size={18} /> Home</button>
        <h1 className="text-xl font-extrabold text-white">{isFinal || readOnly ? "Your Locked XI" : isLocked ? "Squad Submitted" : "Build Your XI"}</h1>
        {selectedDay && (
          <p className="mt-0.5 text-sm font-semibold text-amber-200" data-testid="builder-match-day">{fullDayLabel(selectedDay)}</p>
        )}
        {!readOnly && lockTime && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-black/30 px-3 py-1 text-sm font-bold text-white" data-testid="builder-countdown">
            <Clock size={14} className="text-amber-300" /> {cd.passed ? "Locked" : `Locks in ${cd.label}`}
          </div>
        )}
      </div>

      <div className="mx-auto max-w-md px-4 pt-4">
        {loading ? (
          <div className="h-80 animate-pulse rounded-2xl bg-secondary/50" />
        ) : days.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card py-16 text-center" data-testid="builder-no-fixtures">
            <CalendarClock size={32} className="mx-auto mb-3 text-amber-400" />
            <p className="text-base font-bold">No upcoming fixtures yet</p>
            <p className="mx-auto mt-1 max-w-xs text-sm text-muted-foreground">The squad builder opens as soon as the Football World Cup 2026 schedule goes live. Check back soon!</p>
            <button onClick={() => navigate("/football/matches")} className="mt-4 rounded-xl wc-bg-gold px-5 py-2.5 text-sm font-extrabold text-slate-900" data-testid="builder-view-matches">View Fixtures</button>
          </div>
        ) : (
          <>
            <DayPicker days={days} selected={selectedDay} onSelect={setSelectedDay} />

            {/* Team name (user-level, editable when building/editing) */}
            <div className="mb-3 rounded-xl border border-border bg-card px-3 py-2.5" data-testid="team-name-field">
              <label className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                <Pencil size={11} className="wc-gold" /> Team Name
              </label>
              <input
                value={teamName}
                onChange={(e) => onTeamNameChange(e.target.value)}
                disabled={readOnly || isFinal}
                maxLength={30}
                placeholder="Name your XI"
                className="w-full bg-transparent text-base font-extrabold text-foreground outline-none disabled:opacity-70"
                data-testid="team-name-input"
              />
            </div>

            {submittedView && (
              <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2.5 text-sm font-semibold text-amber-200" data-testid="submitted-banner">
                <CheckCircle2 size={16} /> Squad submitted. Edit until kickoff at {lockLabel}.
              </div>
            )}

            <BudgetTracker squad={squad} />

            {/* FOOTIE / Clear All controls */}
            {canEdit && (
              <div className="my-3 flex items-center gap-2" data-testid="footie-controls">
                {filled === 0 ? (
                  <button
                    onClick={() => setFootieOpen(true)}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-amber-400/50 bg-amber-400/10 py-3 text-sm font-extrabold text-amber-200"
                    data-testid="footie-build-btn"
                  >
                    <FootieBall size={20} /> Build with FOOTIE
                  </button>
                ) : (
                  <>
                    {isAiBuilt && (
                      <button
                        onClick={() => setFootieOpen(true)}
                        className="flex items-center justify-center gap-1.5 rounded-xl border border-amber-400/50 bg-amber-400/10 px-3 py-2 text-sm font-bold text-amber-200"
                        data-testid="footie-roll-again-btn"
                      >
                        <RotateCw size={15} /> Roll again
                      </button>
                    )}
                    <button
                      onClick={() => setClearConfirm(true)}
                      className="flex items-center justify-center gap-1.5 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm font-bold text-red-300"
                      data-testid="clear-all-btn"
                    >
                      <Trash2 size={15} /> Clear All
                    </button>
                    {isAiBuilt && (
                      <span className="ml-auto flex items-center gap-1 text-[11px] font-bold text-amber-300/80" data-testid="footie-built-badge">
                        <Sparkles size={12} /> FOOTIE-built
                      </span>
                    )}
                  </>
                )}
              </div>
            )}

            {/* position counters */}
            <div className="my-3 grid grid-cols-4 gap-2" data-testid="position-counters">
              {Object.keys(POSITION_RULES).map((pos) => {
                const c = counts[pos] || 0; const { min, max } = POSITION_RULES[pos];
                const ok = c >= min && c <= max;
                return (
                  <div key={pos} className={`rounded-lg border p-2 text-center ${ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`}>
                    <div className="text-xs text-muted-foreground">{pos}</div>
                    <div className="text-sm font-extrabold">{c}<span className="text-[10px] font-normal text-muted-foreground">/{min}-{max}</span></div>
                  </div>
                );
              })}
            </div>

            {/* tabs */}
            <MatchDayFixturesStrip fixtures={dayFixtures} teams={teams} />
            <div className="mb-3 flex gap-2 rounded-xl border border-border bg-card p-1">
              {["pitch", "captain"].map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`flex-1 rounded-lg py-2 text-sm font-bold ${tab === t ? "wc-bg-gold text-slate-900" : "text-muted-foreground"}`} data-testid={`builder-tab-${t}`}>
                  {t === "pitch" ? "Starting XI" : "Captain"}
                </button>
              ))}
            </div>

            {tab === "pitch" ? (
              <>
                {canEdit && (
                  <div className="mb-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="formation-picker">
                    {FORMATION_LIST.map((f) => (
                      <button
                        key={f}
                        onClick={() => changeFormation(f)}
                        className={`shrink-0 rounded-lg border px-3.5 py-1.5 text-sm font-extrabold transition-all ${formation === f ? "wc-bg-gold border-amber-400 text-slate-900" : "border-border bg-card text-muted-foreground"}`}
                        data-testid={`formation-pill-${f}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                )}
                <PitchView squad={squad} emptySlots={emptySlots} onEmptyPress={openAdd} onSlotPress={canEdit ? openSwap : undefined} showPoints={scored} />
              </>
            ) : (
              <CaptainSelector squad={squad} onSetCaptain={canEdit ? setCaptain : undefined} onSetVC={canEdit ? setVC : undefined} />
            )}
          </>
        )}

        {/* Contextual Shop nudges while building (before lock) */}
        {canEdit && !isLocked && (
          <div className="mt-4 space-y-2" data-testid="shop-nudges">
            {inventory.super_sub === 0 && filled >= 11 && (
              <button onClick={() => navigate("/subscribe")} className="flex w-full items-center justify-between rounded-xl border border-amber-400/30 bg-amber-400/5 px-3.5 py-2.5 text-left" data-testid="nudge-supersub">
                <span className="text-xs text-amber-100/90">Add a 12th player? Get <span className="font-bold">Super Subs</span> in the Shop.</span>
                <span className="shrink-0 text-xs font-extrabold wc-gold">Shop →</span>
              </button>
            )}
            {inventory.no_penalty_shield === 0 && (
              <button onClick={() => navigate("/subscribe")} className="flex w-full items-center justify-between rounded-xl border border-amber-400/30 bg-amber-400/5 px-3.5 py-2.5 text-left" data-testid="nudge-shield">
                <span className="text-xs text-amber-100/90">Protect against red cards &amp; own goals. <span className="font-bold">Shop Penalty Shield</span>.</span>
                <span className="shrink-0 text-xs font-extrabold wc-gold">Shop →</span>
              </button>
            )}
          </div>
        )}

        {isLocked && !isFinal && !readOnly && (
          <PowerUpsPanel
            selectedDay={selectedDay}
            squad={squad}
            teams={teams}
            budgetRemaining={budgetRemaining}
            consumablesUsed={consumablesUsed}
            onChanged={loadSquad}
          />
        )}
      </div>

      {/* Sticky action bar — pinned ABOVE the bottom navigation */}
      {showLockBar && (
        <div
          className="fixed left-0 right-0 z-[60] border-t border-border bg-card/95 p-3 backdrop-blur"
          style={{ bottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-auto max-w-md">
            {submittedView ? (
              <button onClick={() => setEditMode(true)} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-400/10 py-3.5 text-base font-extrabold text-amber-200" data-testid="edit-squad-btn">
                <Pencil size={18} /> Edit Squad
              </button>
            ) : (
              <>
                {errors.length > 0 && <div className="mb-2 text-center text-xs text-amber-400" data-testid="lock-errors">{errors[0]}</div>}
                <button onClick={doLock} disabled={!canLock} className="flex w-full items-center justify-center gap-2 rounded-xl wc-bg-gold py-3.5 text-base font-extrabold text-slate-900 disabled:opacity-40" data-testid="lock-squad-btn">
                  <Lock size={18} /> Lock Squad
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <PlayerPickerModal
        open={picker.open}
        onClose={() => setPicker({ open: false, position: null, mode: "add", current: null })}
        position={picker.position}
        mode={picker.mode}
        currentPlayer={picker.current}
        pickedIds={pickedIds}
        budgetRemaining={budgetRemaining}
        teams={teams}
        onPick={handlePick}
        onRemove={handleRemove}
      />
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />

      <FootieBuildModal open={footieOpen} onClose={() => setFootieOpen(false)} onBuild={buildWithFootie} building={footieBuilding} />

      {/* Clear All confirmation */}
      {clearConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" data-testid="clear-confirm">
          <div className="w-full max-w-sm rounded-2xl border border-red-500/30 bg-slate-800 p-5">
            <h2 className="text-lg font-extrabold text-white">Clear all players?</h2>
            <p className="mt-2 text-sm text-slate-300">This will remove all 11 players, captain, and vice captain. You can rebuild from scratch.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setClearConfirm(false)} className="flex-1 rounded-xl border border-slate-600 py-2.5 text-sm font-bold text-slate-300" data-testid="clear-cancel">Cancel</button>
              <button onClick={clearAll} className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-extrabold text-white" data-testid="clear-confirm-ok">Yes, clear</button>
            </div>
          </div>
        </div>
      )}

      {/* FOOTIE build loading overlay */}
      {footieLoading && (
        <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="footie-loading">
          <div className="footie-spin"><FootieBall size={84} /></div>
          <p className="mt-4 text-base font-extrabold text-amber-200">FOOTIE is picking your XI…</p>
        </div>
      )}
    </div>
  );
}
